import { FC } from "react";
import { VideoCameraIcon } from "@heroicons/react/24/outline";

import { clsxMerge } from "shared/lib/helpers";
import { getSafeHttpUrl } from "shared/lib/helpers/url";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

interface JoinMeetingLinkPropsI extends PropsWithClassNameI {
  conferenceUrl: string | undefined;
}

/**
 * The one way into the meeting from the account card.
 *
 * Renders nothing without a usable link, which is the common case rather than
 * the exception: Exchange and plain Zoom invites publish no join URL in the
 * .ics at all, and no meeting booked before we started capturing it has one.
 * Callers must not treat this as the only affordance on the card.
 *
 * Shared by the tooltip, the ribbon and the details section so the three
 * surfaces cannot drift into offering different things for the same meeting.
 */
export const JoinMeetingLink: FC<JoinMeetingLinkPropsI> = ({
  className,
  conferenceUrl,
}) => {
  const safeUrl = getSafeHttpUrl(conferenceUrl);

  if (!safeUrl) {
    return null;
  }

  return (
    <a
      className={clsxMerge(
        "flex w-fit items-center gap-1.5 text-[#4474E3] typography-body-5",
        className
      )}
      href={safeUrl}
      target="_blank"
      rel="noopener noreferrer"
      // The card lives inside a tooltip on one surface; without this a click
      // bubbles to the row underneath and opens the account instead.
      onClick={(e) => e.stopPropagation()}
    >
      <VideoCameraIcon className="h-4 w-4" />
      Join meeting
    </a>
  );
};
